import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  Box,
  useTheme,
  FormHelperText,
} from "@mui/material";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import moment from "moment";
import { Save } from "@mui/icons-material";
import Swal from "sweetalert2";
import { updateFinancialTransaction } from "../../api/financialTransactions/updateFinancialTransactions";
import { DocumentType, PaymentType } from "../../components/enums/enums";

const EditFinancialTransactionModal = ({
  open,
  onClose,
  taxpayerList,
  transactionData,
  setSearchForTransaction,
  isCustomercard = false,
}) => {
  const theme = useTheme();

  const [formData, setFormData] = useState({
    id: "",
    date: moment(),
    documentType: DocumentType.TAHAKKUK,
    description: "",
    expense: "",
    collection: "",
    accrual: "",
    paymentType: PaymentType.NAKIT,
    taxpayerId: null,
  });

  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (transactionData && open) {
      setFormData({
        id: transactionData.id,
        date: moment(transactionData.date),
        documentType: transactionData.documentType,
        description: transactionData.description,
        expense: transactionData.expense === 0 ? "" : transactionData.expense,
        collection:
          transactionData.collection === 0 ? "" : transactionData.collection,
        accrual: transactionData.accrual === 0 ? "" : transactionData.accrual,
        paymentType: transactionData.paymentType,
        taxpayerId: transactionData.taxpayerId,
      });
    }
  }, [transactionData, open]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    let updatedFormData = { ...formData, [name]: value };

    if (name === "documentType") {
      if (value === DocumentType.TAHSILAT) {
        updatedFormData = {
          ...updatedFormData,
          expense: "",
          accrual: "",
        };
      } else if (value === DocumentType.TEDIYE) {
        updatedFormData = {
          ...updatedFormData,
          collection: "",
          accrual: "",
        };
      } else if (value === "TAHAKKUK_AIDAT") {
        updatedFormData = {
          ...updatedFormData,
          expense: "",
          collection: "",
        };
      }
    }

    setFormData(updatedFormData);
  };

  const handleDateChange = (newDate) => {
    setFormData({
      ...formData,
      date: newDate,
    });
  };

  const handleNumberChange = (e) => {
    const { name, value } = e.target;
    if (value === "" || !isNaN(parseFloat(value))) {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
  };

  const handleTaxpayerChange = (event, newValue) => {
    setFormData({
      ...formData,
      taxpayerId: newValue ? newValue.id : null,
    });
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.date) newErrors.date = "Tarih alanı zorunludur";
    if (!formData.taxpayerId)
      newErrors.taxpayerId = "Mükellef seçimi zorunludur";
    if (!formData.documentType)
      newErrors.documentType = "İşlem Türü seçimi zorunludur";
    if (!formData.description)
      newErrors.description = "Açıklama alanı zorunludur";
    if (
      formData.documentType === DocumentType.TAHSILAT &&
      formData.collection === ""
    ) {
      newErrors.collection = "Alınan değeri girilmelidir";
    }
    if (
      formData.documentType === DocumentType.TEDIYE &&
      formData.expense === ""
    ) {
      newErrors.expense = "Harcama değeri girilmelidir";
    }
    if (
      (formData.documentType === "TAHAKKUK_AIDAT" ||
        formData.documentType === DocumentType.TAHAKKUK ||
        formData.documentType === DocumentType.TAHAKKUK_SGK ||
        formData.documentType === DocumentType.TAHAKKUK_VERGI ||
        formData.documentType === DocumentType.TAHAKKUK_STOPAJ ||
        formData.documentType === DocumentType.TAHAKKUK_DIGER) &&
      formData.accrual === ""
    ) {
      newErrors.accrual = "Borç değeri girilmelidir";
    }
    if (
      (formData.documentType === DocumentType.TAHSILAT ||
        formData.documentType === DocumentType.TEDIYE) &&
      !formData.paymentType
    ) {
      newErrors.paymentType = "Ödeme tipi seçimi zorunludur";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    setSubmitted(true);

    if (!validateForm()) {
      setTimeout(() => {
        setSubmitted(false);
      }, 3000);
      return;
    }

    const requestData = {
      ...formData,
      date: moment(formData.date).format("YYYY-MM-DD"),
      expense: formData.expense === "" ? 0 : parseFloat(formData.expense),
      collection:
        formData.collection === "" ? 0 : parseFloat(formData.collection),
      accrual: formData.accrual === "" ? 0 : parseFloat(formData.accrual),
      paymentType:
        formData.documentType === DocumentType.TAHSILAT ||
        formData.documentType === DocumentType.TEDIYE
          ? formData.paymentType
          : undefined,
    };

    try {
      await updateFinancialTransaction(requestData)
        .then((res) => {
          Swal.fire({
            text: "Başarıyla Güncellendi.",
            icon: "success",
            confirmButtonText: "Tamam",
          });
        })
        .catch((err) => {
          Swal.fire({
            text: "İşlem güncelleme hata oluştu.",
            icon: "error",
            confirmButtonText: "Tamam",
          });
        });
      setSearchForTransaction(true);
      onClose();
    } catch (err) {
      Swal.fire({
        text: "İşlem güncellenirken hata oluştu.",
        icon: "error",
        confirmButtonText: "Tamam",
      });
    }
  };

  const currentTaxpayer =
    taxpayerList?.find((taxpayer) => taxpayer.id === formData.taxpayerId) ||
    null;

  const inputStyle = {
    marginBottom: "12px",
    "& .MuiInputBase-root": {
      height: "42px",
    },
    "& .MuiInputLabel-root": {
      transform: "translate(14px, 12px) scale(1)",
      "&.MuiInputLabel-shrink": {
        transform: "translate(14px, -9px) scale(0.75)",
      },
    },
  };

  const numberInputStyle = {
    ...inputStyle,
    width: "31%",
  };

  const isTahsilat = formData.documentType === DocumentType.TAHSILAT;
  const isTediye = formData.documentType === DocumentType.TEDIYE;
  const isTahakkukAidat = formData.documentType === "TAHAKKUK_AIDAT";
  const showPaymentType = isTahsilat || isTediye;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        style: {
          borderRadius: "12px",
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
        },
      }}
    >
      <DialogTitle
        sx={{
          background: theme.palette.primary.main,
          color: "white",
          fontWeight: 500,
          padding: "6px 24px",
        }}
      >
        Düzenle
      </DialogTitle>
      <DialogContent sx={{ padding: "20px", mt: "12px" }}>
        <LocalizationProvider dateAdapter={AdapterMoment}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            <Box sx={{ height: "auto", paddingTop: "5px" }}>
              <DatePicker
                label="Tarih"
                value={formData.date}
                onChange={handleDateChange}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: "small",
                    required: true,
                    error: submitted && !!errors.date,
                    helperText: submitted && errors.date,
                    sx: {
                      marginBottom: "12px",
                      "& .MuiInputBase-root": {
                        minHeight: "36px",
                      },
                      "& .MuiInputLabel-root": {
                        fontSize: "0.875rem",
                        lineHeight: "2.1",
                      },
                    },
                  },
                }}
              />
            </Box>

            <Autocomplete
              options={taxpayerList || []}
              getOptionLabel={(option) => option.title || ""}
              value={currentTaxpayer}
              disabled={isCustomercard}
              onChange={handleTaxpayerChange}
              noOptionsText="Mükellef bulunamadı"
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Mükellef"
                  fullWidth
                  required
                  size="small"
                  error={submitted && !!errors.taxpayerId}
                  helperText={submitted && errors.taxpayerId}
                  sx={inputStyle}
                />
              )}
            />

            <FormControl
              fullWidth
              size="small"
              sx={inputStyle}
              required
              error={submitted && !!errors.documentType}
            >
              <InputLabel>İşlem Türü</InputLabel>
              {isTahakkukAidat ? (
                <TextField
                  value="TAHAKKUK_AIDAT"
                  InputProps={{
                    readOnly: true,
                  }}
                  variant="outlined"
                  size="small"
                  disabled
                />
              ) : (
                <Select
                  name="documentType"
                  value={formData.documentType}
                  onChange={handleChange}
                  label="İşlem Türü"
                >
                  {Object.entries(DocumentType).map(
                    ([key, value]) =>
                      value !== "TAHAKKUK_AIDAT" && (
                        <MenuItem key={key} value={value}>
                          {value}
                        </MenuItem>
                      )
                  )}
                </Select>
              )}
              {submitted && errors.documentType && (
                <FormHelperText>{errors.documentType}</FormHelperText>
              )}
            </FormControl>

            <TextField
              name="description"
              label="Açıklama"
              value={formData.description}
              onChange={handleChange}
              fullWidth
              required
              size="small"
              error={submitted && !!errors.description}
              helperText={submitted && errors.description}
              sx={inputStyle}
            />

            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <TextField
                name="expense"
                label="Harcama"
                type="number"
                value={formData.expense}
                onChange={handleNumberChange}
                size="small"
                placeholder="0"
                InputLabelProps={{ shrink: true }}
                sx={numberInputStyle}
                disabled={formData.documentType === DocumentType.TAHSILAT}
                error={submitted && !!errors.expense}
                helperText={submitted && errors.expense}
              />

              <TextField
                name="collection"
                label="Alınan"
                type="number"
                value={formData.collection}
                onChange={handleNumberChange}
                size="small"
                placeholder="0"
                InputLabelProps={{ shrink: true }}
                sx={numberInputStyle}
                disabled={!isTahsilat}
                error={submitted && !!errors.collection}
                helperText={submitted && errors.collection}
              />

              <TextField
                name="accrual"
                label="Borç"
                type="number"
                value={formData.accrual}
                onChange={handleNumberChange}
                size="small"
                placeholder="0"
                InputLabelProps={{ shrink: true }}
                sx={numberInputStyle}
                disabled={isTahsilat}
                error={submitted && !!errors.accrual}
                helperText={submitted && errors.accrual}
              />
            </Box>

            {showPaymentType && (
              <FormControl
                fullWidth
                size="small"
                sx={inputStyle}
                required
                error={submitted && !!errors.paymentType}
              >
                <InputLabel>Ödeme Tipi</InputLabel>
                <Select
                  name="paymentType"
                  value={formData.paymentType}
                  onChange={handleChange}
                  label="Ödeme Tipi"
                >
                  {Object.keys(PaymentType).map((type) => (
                    <MenuItem key={type} value={PaymentType[type]}>
                      {PaymentType[type]}
                    </MenuItem>
                  ))}
                </Select>
                {submitted && errors.paymentType && (
                  <FormHelperText>{errors.paymentType}</FormHelperText>
                )}
              </FormControl>
            )}
          </Box>
        </LocalizationProvider>
      </DialogContent>
      <DialogActions
        sx={{ padding: "16px 24px", justifyContent: "flex-end", gap: "12px" }}
      >
        <Button
          onClick={onClose}
          color="inherit"
          variant="outlined"
          sx={{
            borderRadius: "8px",
            textTransform: "none",
            fontWeight: 500,
            minWidth: "100px",
          }}
        >
          İptal
        </Button>
        <Button
          onClick={handleSubmit}
          color="primary"
          variant="contained"
          startIcon={<Save />}
          sx={{
            borderRadius: "8px",
            textTransform: "none",
            fontWeight: 500,
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
            minWidth: "100px",
          }}
        >
          Kaydet
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditFinancialTransactionModal;
